import { useState, FormEvent, ChangeEvent } from 'react';
import EmployeeService from '../../AppSettings/services/EmployeeService';
import type { Employees, Permission } from '../../types/team';

/**
 * Enable sign-in for a FIELD MEMBER (plan field-members-and-signin-conversion, Build C):
 * LINKS the person's new Entra identity (their oid) to the row they already are —
 * employeeid never changes, so every phase assignment, roster entry and attribution they
 * ever appeared in survives conversion untouched. The admin supplies the oid directly
 * (decision 3: exact, parity with how user rows have always been keyed) and picks the role
 * the person becomes a user with.
 */
interface EnableSigninModalProps {
  isOpen: boolean;
  member: Employees | null;
  permissions: Permission[];
  onClose: () => void;
  onEnabled: () => void;
}

const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const EnableSigninModal = ({
  isOpen,
  member,
  permissions,
  onClose,
  onEnabled,
}: EnableSigninModalProps): JSX.Element | null => {
  const [entraOid, setEntraOid] = useState('');
  const [permishId, setPermishId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const close = (): void => {
    // A refusal must not replay on the next open (the AddFieldMemberModal rule).
    setError(null);
    setEntraOid('');
    setPermishId('');
    onClose();
  };

  const handleEnable = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (submitting || !member) return;
    const oid = entraOid.trim().toLowerCase();
    if (!UUID_SHAPE.test(oid)) {
      setError('The Entra object ID must be a UUID — copy it from the person’s Entra profile.');
      return;
    }
    if (!permishId) {
      setError('Pick the role the person signs in as.');
      return;
    }
    setSubmitting(true);
    try {
      await EmployeeService.enableSignin(member.EmployeeID, oid, parseInt(permishId, 10));
      await onEnabled();
      close();
    } catch (err) {
      // The server names the refusal ("already belongs to someone", "already enabled").
      setError((err as Error)?.message || 'Could not enable sign-in. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !member) {
    return null;
  }

  return (
    <div className="team-new-contact-form-overlay">
      <div className="team-new-contact-form">
        <h3>Enable sign-in — {member.FirstName} {member.LastName}</h3>
        <p className="team-field-member-note">
          Links their Entra account to the record they already are. Everything they were ever
          assigned to stays theirs — nothing moves.
        </p>
        <form onSubmit={handleEnable}>
          <div className="team-form-group">
            <input
              type="text"
              placeholder="Entra object ID (oid)"
              aria-label="Entra object ID"
              maxLength={36}
              value={entraOid}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setEntraOid(e.target.value)}
              required
            />
            <p className="team-form-hint">
              Find it in the{' '}
              <a
                href="https://entra.microsoft.com/#view/Microsoft_AAD_UsersAndTenants/UserManagementMenuBlade/~/AllUsers"
                target="_blank"
                rel="noopener noreferrer"
              >
                Microsoft Entra admin center
              </a>
              {' '}— open Users, select the person, and copy their Object ID.
            </p>
          </div>
          <div className="team-form-group">
            <select
              aria-label="Role"
              value={permishId}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => setPermishId(e.target.value)}
              required
            >
              <option value="">Role…</option>
              {permissions.map((p) => (
                <option key={p.permish_id} value={p.permish_id}>{p.role}</option>
              ))}
            </select>
          </div>
          {error && <p className="team-form-error" role="alert">{error}</p>}
          <div className="team-form-actions">
            <button type="submit" className="team-submit-button" disabled={submitting}>
              Enable sign-in
            </button>
            <button type="button" className="team-cancel-button" onClick={close}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EnableSigninModal;
